import React from 'react';
import { Layout, Breadcrumb, Badge, Dropdown, Button, Space, Typography } from 'antd';
import { useLocation } from 'react-router-dom';
import {
  BellOutlined,
  FullscreenOutlined,
  FullscreenExitOutlined,
  ReloadOutlined,
  MenuOutlined,
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { dashboardApi } from '../../services/api';

const { Header: AntHeader } = Layout;
const { Text } = Typography;

interface HeaderProps {
  isMobile: boolean;
  onMenuClick: () => void;
}

const Header: React.FC<HeaderProps> = ({ isMobile, onMenuClick }) => {
  const location = useLocation();
  const [isFullscreen, setIsFullscreen] = React.useState(false);

  // 获取通知数据
  const { data: _notifications } = useQuery({
    queryKey: ['notifications'],
    queryFn: () => dashboardApi.getStats(),
    refetchInterval: 30000,
  });

  // 面包屑映射
  const breadcrumbMap: Record<string, string> = {
    '/dashboard': '仪表板',
    '/products': '商品管理',
    '/groups': '群组管理',
    '/users': '用户管理',
    '/merchants': '商户管理',
    '/payments': '订单管理',
    '/distribution': '分销中心',
    '/analytics': '数据分析',
    '/settings': '系统设置',
  };

  const getBreadcrumbs = () => {
    const pathSnippets = location.pathname.split('/').filter(i => i);
    const breadcrumbItems = [{ title: '首页' }];
    pathSnippets.forEach((_, index) => {
      const url = `/${pathSnippets.slice(0, index + 1).join('/')}`;
      const title = breadcrumbMap[url];
      if (title) {
        breadcrumbItems.push({ title });
      }
    });
    return breadcrumbItems;
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  const notificationMenu = (
    <div className="w-80 max-h-96 overflow-y-auto">
      <div className="p-4 border-b">
        <div className="flex items-center justify-between">
          <Text strong>通知中心</Text>
          <Text type="secondary" className="text-xs">
            共 {0} 条未读
          </Text>
        </div>
      </div>
      <div className="p-2">
        {[
          { id: 1, title: '新用户注册', content: '用户 @user123 刚刚注册了账号', time: '2分钟前', type: 'info' },
          { id: 2, title: '支付成功', content: '订单 #12345 支付成功，金额 ¥99.00', time: '5分钟前', type: 'success' },
          { id: 3, title: '系统警告', content: '服务器CPU使用率超过80%', time: '10分钟前', type: 'warning' },
        ].map(notification => (
          <div
            key={notification.id}
            className="p-3 hover:bg-gray-50 rounded-lg cursor-pointer border-b border-gray-100 last:border-b-0"
          >
            <div className="flex items-start space-x-3">
              <div
                className={`w-2 h-2 rounded-full mt-2 ${
                  notification.type === 'success'
                    ? 'bg-green-500'
                    : notification.type === 'warning'
                    ? 'bg-yellow-500'
                    : 'bg-blue-500'
                }`}
              />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-900 mb-1">{notification.title}</div>
                <div className="text-xs text-gray-500 mb-1">{notification.content}</div>
                <div className="text-xs text-gray-400">{notification.time}</div>
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="p-3 border-t text-center">
        <Button type="link" size="small">查看所有通知</Button>
      </div>
    </div>
  );

  return (
    <AntHeader className="layout-header bg-white px-4 flex items-center justify-between" style={{ paddingLeft: isMobile ? 12 : 24, paddingRight: isMobile ? 12 : 24 }}>
      <div className="flex items-center flex-1 min-w-0">
        {isMobile && (
          <Button
            type="text"
            icon={<MenuOutlined />}
            onClick={onMenuClick}
            className="mr-2"
            style={{ fontSize: 18 }}
          />
        )}
        {!isMobile && <Breadcrumb items={getBreadcrumbs()} />}
        {isMobile && (
          <span className="text-sm font-medium text-gray-700 truncate">
            {breadcrumbMap[location.pathname] || '首页'}
          </span>
        )}
      </div>

      <div className="flex items-center space-x-2">
        {/* 系统状态（仅桌面端） */}
        {!isMobile && (
          <div className="flex items-center space-x-2 mr-2">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
            <Text type="secondary" className="text-sm">系统正常</Text>
          </div>
        )}

        <Space size={isMobile ? 4 : 8}>
          {!isMobile && (
            <>
              <Button type="text" icon={<ReloadOutlined />} onClick={handleRefresh} title="刷新页面" />
              <Button
                type="text"
                icon={isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
                onClick={toggleFullscreen}
                title={isFullscreen ? '退出全屏' : '全屏显示'}
              />
            </>
          )}
          <Dropdown overlay={notificationMenu} trigger={['click']} placement="bottomRight" arrow>
            <Button type="text" className="relative">
              <Badge count={0} size="small">
                <BellOutlined className="text-lg" />
              </Badge>
            </Button>
          </Dropdown>
        </Space>
      </div>
    </AntHeader>
  );
};

export default Header;
